import { useEffect, useState } from "react";
import { ClipboardText as ClipboardCheck, CheckCircle, Warning, X, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";
import { http } from "@/lib/api";

/* Mobile-first FMCSA-compliant DVIR form. Driver picks pre/post-trip, taps
 * "Mark Defect" on any failing item, optional note, signs with name,
 * GPS captured one-shot at submit time. */

function getOneShotGPS() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy_m: pos.coords.accuracy }),
      () => resolve(null),
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 8000 },
    );
  });
}

const ITEM_LABELS = {
  service_brakes: "Service Brakes", parking_brake: "Parking Brake", steering: "Steering",
  lighting_devices: "Lights & Reflectors", tires: "Tires", horn: "Horn",
  windshield_wipers: "Windshield Wipers", rear_vision_mirrors: "Mirrors", coupling_devices: "Coupling Devices",
  wheels_rims: "Wheels & Rims", emergency_equipment: "Emergency Equipment",
  air_brake_system: "Air Brake System", brake_warning_devices: "Brake Warning Lights",
  transmission: "Transmission", drive_line: "Drive Line / U-Joints", exhaust_system: "Exhaust System",
  fuel_system: "Fuel System", frame: "Frame & Cross Members", battery: "Battery",
  suspension: "Suspension / Air Bags", seat_belts: "Seat Belts", instrument_panel: "Instrument Panel",
  dash_warning_indicators: "Dash Warning Lights", fluid_levels: "Fluid Levels",
  doors_locks: "Doors & Locks", fifth_wheel: "5th Wheel", trailer_lights: "Trailer Lights",
  trailer_brakes: "Trailer Brakes", trailer_tires_wheels: "Trailer Tires/Wheels", load_securement: "Load Securement",
};

export default function DriverDVIRPanel({ assignedAssetId, assignedAssetName, driverName }) {
  const [open, setOpen] = useState(false);
  const [inspectionType, setInspectionType] = useState("pre_trip");
  const [items, setItems] = useState([]); // canonical list from /api/dvir/checklist
  const [defectMap, setDefectMap] = useState({}); // {item: {severity, note}}
  const [odometer, setOdometer] = useState("");
  const [signature, setSignature] = useState(driverName || "");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [lastSubmitted, setLastSubmitted] = useState(null);

  useEffect(() => {
    if (open && items.length === 0) {
      http.get("/dvir/checklist").then((r) => setItems(r.data.items)).catch(() => setItems([]));
    }
  }, [open, items.length]);

  const reset = () => {
    setDefectMap({}); setOdometer(""); setNotes("");
  };

  const submit = async () => {
    if (!assignedAssetId) { toast.error("Start a shift on an asset before submitting a DVIR"); return; }
    if (!signature.trim()) { toast.error("Sign your name"); return; }
    setSubmitting(true);
    try {
      const gps = await getOneShotGPS();
      const defects = Object.entries(defectMap).map(([item, v]) => ({
        item, severity: v.severity || "minor", note: v.note || null,
      }));
      const body = {
        asset_id: assignedAssetId,
        inspection_type: inspectionType,
        defects,
        odometer_miles: odometer ? parseInt(odometer, 10) : null,
        signature_name: signature.trim(),
        notes: notes.trim() || null,
        ...(gps || {}),
      };
      const r = await http.post("/dvir", body);
      setLastSubmitted({ id: r.data.id, status: r.data.status, type: inspectionType });
      const label = r.data.status === "passed" ? "submitted · vehicle satisfactory"
                  : r.data.status === "out_of_service" ? "submitted · vehicle OUT OF SERVICE"
                  : "submitted · defects logged";
      toast.success(`DVIR ${label}`);
      reset();
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "DVIR submission failed");
    } finally { setSubmitting(false); }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="w-full bg-[#0d0d0d] border-2 border-[#4682b4] hover:bg-[#4682b4]/10 px-4 py-3 mb-5 flex items-center justify-between gap-2 transition-colors"
        data-testid="driver-dvir-open"
      >
        <div className="flex items-center gap-3">
          <ClipboardCheck size={20} className="text-[#4682b4]" weight="bold" />
          <div className="text-left">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#4682b4] font-bold">FMCSA · Inspection</div>
            <div className="font-heading font-black text-base tracking-tight">Daily Vehicle Inspection (DVIR)</div>
          </div>
        </div>
        {lastSubmitted && (
          <span className="text-[9px] uppercase tracking-[0.25em] font-black px-1.5 py-0.5"
            style={{ background: lastSubmitted.status === "passed" ? "#22c55e" : lastSubmitted.status === "out_of_service" ? "#ef4444" : "#ff8c00", color: "#000" }}>
            ✓ {lastSubmitted.type.replace("_", "-")} {lastSubmitted.status}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-0 bg-black/85 z-[1000] flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setOpen(false)} data-testid="dvir-form-sheet">
          <div className="bg-[#0d0d0d] border border-[#2a2a2a] w-full max-w-md max-h-[94vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-[#2a2a2a] flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a]">DVIR · {assignedAssetName || "—"}</div>
                <h2 className="font-heading font-black text-lg tracking-tight">Vehicle Inspection</h2>
              </div>
              <button onClick={() => setOpen(false)} className="text-[#71717a] hover:text-white" data-testid="dvir-form-close"><X size={16} weight="bold" /></button>
            </div>

            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-2">
                {["pre_trip", "post_trip"].map((t) => (
                  <button key={t} onClick={() => setInspectionType(t)}
                    className={`py-3 text-[11px] uppercase tracking-[0.25em] font-black border-2 transition-colors ${inspectionType === t ? "border-[#ff8c00] bg-[#ff8c00] text-black" : "border-[#2a2a2a] text-[#a1a1aa] hover:border-[#ff8c00]"}`}
                    data-testid={`dvir-type-${t}`}>
                    {t.replace("_", "-")}
                  </button>
                ))}
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-[0.25em] text-[#71717a] block mb-1">Odometer</label>
                <input type="number" placeholder="miles" value={odometer} onChange={(e) => setOdometer(e.target.value)} className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] outline-none text-sm font-mono" data-testid="dvir-odometer" />
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a] mb-2 flex items-center justify-between">
                  <span>Inspection Items ({items.length})</span>
                  <span className="text-[#ef4444]">{Object.keys(defectMap).length} defect{Object.keys(defectMap).length === 1 ? "" : "s"}</span>
                </div>
                <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
                  {items.map((it) => {
                    const flagged = defectMap[it];
                    return (
                      <div key={it} className="bg-[#1a1a1a] border border-[#2a2a2a] p-2" data-testid={`dvir-item-${it}`}>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm">{ITEM_LABELS[it] || it.replace(/_/g, " ")}</span>
                          {flagged ? (
                            <button onClick={() => { const m = { ...defectMap }; delete m[it]; setDefectMap(m); }}
                              className="text-[9px] uppercase tracking-[0.25em] font-black px-2 py-0.5 bg-[#ef4444] text-black" data-testid={`dvir-clear-${it}`}>
                              <Warning size={9} weight="fill" className="inline mr-1" /> Defect
                            </button>
                          ) : (
                            <button onClick={() => setDefectMap({ ...defectMap, [it]: { severity: "minor", note: "" } })}
                              className="text-[9px] uppercase tracking-[0.25em] font-bold px-2 py-0.5 border border-[#2a2a2a] hover:border-[#ef4444] hover:text-[#ef4444]" data-testid={`dvir-flag-${it}`}>
                              OK
                            </button>
                          )}
                        </div>
                        {flagged && (
                          <div className="mt-2 space-y-1">
                            <div className="flex gap-1">
                              {["minor", "major", "out_of_service"].map((sv) => (
                                <button key={sv} onClick={() => setDefectMap({ ...defectMap, [it]: { ...flagged, severity: sv } })}
                                  className={`flex-1 py-1 text-[9px] uppercase tracking-[0.2em] font-bold border ${flagged.severity === sv ? "border-[#ef4444] bg-[#ef4444]/20 text-[#ef4444]" : "border-[#2a2a2a] text-[#71717a]"}`}>
                                  {sv.replace("_", " ")}
                                </button>
                              ))}
                            </div>
                            <input placeholder="Describe the defect…" value={flagged.note} onChange={(e) => setDefectMap({ ...defectMap, [it]: { ...flagged, note: e.target.value } })}
                              className="w-full px-2 py-1.5 bg-[#0d0d0d] border border-[#2a2a2a] outline-none text-xs" />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-[0.25em] text-[#71717a] block mb-1">Notes (optional)</label>
                <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] outline-none text-sm" />
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-[0.25em] text-[#71717a] block mb-1">Signature *</label>
                <input value={signature} onChange={(e) => setSignature(e.target.value)} placeholder="Print your full name" className="w-full px-3 py-2 bg-[#1a1a1a] border-2 border-[#ff8c00] outline-none text-sm font-bold" data-testid="dvir-signature" />
                <div className="text-[10px] text-[#71717a] mt-1">By signing I certify this inspection per 49 CFR § 396.11. GPS will be captured on submit.</div>
              </div>

              <button onClick={submit} disabled={submitting || !signature.trim()} className="w-full bg-[#ff8c00] disabled:opacity-40 text-black font-black py-3 text-[11px] uppercase tracking-[0.25em] flex items-center justify-center gap-2" data-testid="dvir-submit">
                <FloppyDisk size={12} weight="bold" /> {submitting ? "Submitting…" : "Submit Inspection"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
